import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { saveItem } from '../utils/firebaseFunctions';
import Loader from './Loader';
import Sidebar from './Sidebar';
import { toast } from 'react-toastify';
import { firestore } from '../firebase.config';
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
} from 'firebase/firestore';
import { Trash2, Pencil, X } from 'lucide-react';

const HomeSlider = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [sliderList, setSliderList] = useState([]);
  const [uploadedImageUrls, setUploadedImageUrls] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [editId, setEditId] = useState(null);

  // ✅ Ref to scroll the form into view on edit
  const formRef = useRef(null);

  const toggleSidebar = (state) => {
    setIsSidebarOpen(state);
  };

  const cloudName = "dabhatz7m";
  const uploadPreset = "ImageUpload";

  // ✅ Image Upload
  const handleImageUpload = (e) => {
    e.preventDefault();
    const files = document.getElementById("imageInput").files;

    if (!files.length) {
      alert("Please select images!");
      return;
    }

    Array.from(files).forEach((file) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("upload_preset", uploadPreset);
      formData.append("folder", "HomeSlider");

      const xhr = new XMLHttpRequest();
      xhr.open(
        "POST",
        `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
        true
      );

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = (event.loaded / event.total) * 100;
          setUploadProgress(percentComplete);
        }
      };

      xhr.onload = () => {
        if (xhr.status === 200) {
          const data = JSON.parse(xhr.responseText);
          if (data.secure_url) {
            setUploadedImageUrls((prev) => [...prev, data.secure_url]);
          }
          setUploadProgress(0);
        } else {
          toast.error("Upload failed");
          setUploadProgress(0);
        }
      };

      xhr.onerror = () => {
        toast.error("Upload error");
        setUploadProgress(0);
      };

      xhr.send(formData);
    });
  };

  const handleDeleteImage = (index) => {
    const updated = [...uploadedImageUrls];
    updated.splice(index, 1);
    setUploadedImageUrls(updated);
  };

  // ✅ Cancel Edit — clears form state
  const handleCancel = () => {
    setUploadedImageUrls([]);
    setEditId(null);
    setUploadProgress(0);
    // Reset file input
    const fileInput = document.getElementById("imageInput");
    if (fileInput) fileInput.value = "";
  };

  // ✅ Save or Update Slider
  const saveDetails = async () => {
    if (uploadedImageUrls.length === 0) {
      toast.error("Please upload at least one image.");
      return;
    }

    setIsLoading(true);

    try {
      if (editId) {
        await updateDoc(doc(firestore, "HomeSlider", editId), {
          imageUrls: uploadedImageUrls,
        });
        toast.success("Slider updated successfully");
        setEditId(null);
      } else {
        const id = `${Date.now()}`;
        const data = {
          id,
          imageUrls: uploadedImageUrls,
          createdAt: new Date(),
        };
        await saveItem(data);
        toast.success("Slider added successfully");
      }

      setUploadedImageUrls([]);
      // Reset file input after save
      const fileInput = document.getElementById("imageInput");
      if (fileInput) fileInput.value = "";
      fetchSliders();
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      toast.error("Operation failed");
      setIsLoading(false);
    }
  };

  // ✅ Fetch Sliders
  const fetchSliders = async () => {
    try {
      const querySnapshot = await getDocs(
        collection(firestore, "HomeSlider")
      );
      const sliders = [];
      querySnapshot.forEach((docSnap) => {
        sliders.push({ ...docSnap.data(), docId: docSnap.id });
      });
      setSliderList(sliders);
    } catch (error) {
      console.error("Error fetching sliders:", error);
    }
  };

  // ✅ Delete Slider
  const handleDeleteSlider = async (docId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this slider?"
    );
    if (!confirmDelete) return;

    try {
      await deleteDoc(doc(firestore, "HomeSlider", docId));
      toast.success("Deleted successfully");
      fetchSliders();
    } catch (error) {
      toast.error("Delete failed");
    }
  };

  // ✅ Edit Slider — load images into form and scroll to top of form
  const handleEditSlider = (item) => {
    setUploadedImageUrls(item.imageUrls);
    setEditId(item.docId);
    // Scroll the form card into view smoothly
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  useEffect(() => {
    fetchSliders();
  }, []);

  return (
    <div className="lg:flex h-screen bg-white">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />

      <div className={`flex-1 p-6 overflow-y-auto ${isSidebarOpen ? 'ml-56' : 'lg:ml-20'}`}>

        {/* ✅ Upload / Edit Form Section */}
        <div
          ref={formRef}
          className={`max-w-4xl mx-auto bg-white shadow-lg rounded-xl p-6 transition-all duration-300 ${
            editId ? 'ring-2 ring-[#fcaf17] ring-offset-2' : ''
          }`}
        >
          {/* Header with edit indicator badge */}
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-[#fcaf17]">
              {editId ? "Edit Slider Images" : "Add Home Slider Images"}
            </h1>
            {editId && (
              <span className="inline-flex items-center gap-1 bg-yellow-100 text-yellow-800 text-xs font-semibold px-3 py-1 rounded-full border border-yellow-300">
                <Pencil className="w-3 h-3" />
                Editing Mode
              </span>
            )}
          </div>

          {/* Upload Images */}
          <div className="mt-4">
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Slider Images
            </label>

            <label
              htmlFor="imageInput"
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg
                className="mr-2 h-5 w-5 text-[#fcaf17]"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
              {editId ? "Upload New Images" : "Upload Images"}
            </label>

            <input
              type="file"
              id="imageInput"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              className="hidden"
            />

            {/* Upload Progress */}
            {uploadProgress > 0 && (
              <div className="mt-3">
                <div className="bg-gray-200 rounded-full h-2.5 mb-1">
                  <div
                    className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-600 text-center">
                  {Math.round(uploadProgress)}%
                </p>
              </div>
            )}

            {/* Image Preview */}
            {uploadedImageUrls.length > 0 && (
              <div className="flex flex-wrap gap-4 mt-4">
                {uploadedImageUrls.map((url, index) => (
                  <div
                    key={index}
                    className="relative w-[200px] h-[130px] border rounded-md overflow-hidden"
                  >
                    <img
                      src={url}
                      alt="slider"
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={() => handleDeleteImage(index)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ✅ Action Buttons — Save/Update + Cancel */}
          <div className="mt-6 flex items-center gap-3">
            <button
              onClick={saveDetails}
              disabled={isLoading}
              className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isLoading ? <Loader /> : editId ? "Update Slider" : "Save Slider"}
            </button>

            {/* Cancel button — only shown when editing */}
            {editId && (
              <button
                onClick={handleCancel}
                disabled={isLoading}
                className="flex items-center gap-2 bg-gray-100 text-gray-700 px-5 py-2 rounded-lg border border-gray-300 hover:bg-gray-200 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
            )}
          </div>
        </div>

        {/* Slider Table */}
        {sliderList.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold mb-6 text-[#fcaf17]">
              Slider Collection
            </h2>

            <div className="overflow-x-auto border rounded-xl">
              <table className="min-w-full text-sm text-left text-gray-700">
                <thead className="bg-yellow-100 uppercase text-sm font-bold text-orange-800">
                  <tr>
                    <th className="px-6 py-4">Sl. No.</th>
                    <th className="px-6 py-4">Images</th>
                    <th className="px-6 py-4 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sliderList.map((item, index) => (
                    <tr
                      key={item.docId}
                      className={`border-b transition-colors ${
                        editId === item.docId ? 'bg-yellow-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <td className="px-6 py-4 font-semibold">
                        {index + 1}
                      </td>

                      <td className="px-6 py-4">
                        <div className="flex gap-2 flex-wrap">
                          {item.imageUrls?.map((img, i) => (
                            <img
                              key={i}
                              src={img}
                              alt="slider"
                              className="w-28 h-20 object-cover rounded-md border"
                            />
                          ))}
                        </div>
                      </td>

                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center gap-4">
                          <button
                            onClick={() => handleEditSlider(item)}
                            title="Edit Slider"
                            className={`hover:text-blue-800 transition-colors ${
                              editId === item.docId
                                ? 'text-yellow-500'
                                : 'text-blue-600'
                            }`}
                          >
                            <Pencil className="w-5 h-5" />
                          </button>

                          <button
                            onClick={() => handleDeleteSlider(item.docId)}
                            title="Delete Slider"
                            className="text-red-600 hover:text-red-800 transition-colors"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default HomeSlider;
